import {
  AutocompleteWithLabel,
  FreeSoloAutocompleteWithLabel
} from '@adotel/components'
import { Grid, Typography } from '@material-ui/core'
import React from 'react'

import SectionTitle from '../../../components/SectionTitle'
import { getTranslate } from '../../../localization'
import { ScopeOption } from '../types'
import useStyles from './styles/section.styles'

interface ScopeSectionProps {
  propertyCodes: string[]
  roomTypeCodes: string[]
  ratePlanCodes: string[]
  consumableVia: string[]
  onChange: (
    key: 'propertyCodes' | 'roomTypeCodes' | 'ratePlanCodes' | 'consumableVia',
    value: string[]
  ) => void
  propertyOptions: ScopeOption[]
  roomTypeOptions: ScopeOption[]
  ratePlanOptions: ScopeOption[]
  propertiesLoading: boolean
  childrenLoading: boolean
  /** Rendered inside the channel column so the existing field keeps its place in the grid. */
  channelField: React.ReactNode
}

/** The four surfaces a booking can arrive through, as the API spells them. */
const CONSUMABLE_VIA: ScopeOption[] = [
  { value: 'Panel', label: 'Panel' },
  { value: 'Wbs', label: 'Booking engine (WBS)' },
  { value: 'ConnectApi', label: 'Connect API' },
  { value: 'Pms', label: 'PMS' }
]

/**
 * What a typed code may contain.
 *
 * Deliberately permissive: this screen is an editor for codes minted elsewhere (the PMS,
 * the channel manager), so it is in no position to be the authority on their shape. It
 * rejects only what cannot be a code at all — whitespace and the characters that would
 * make one ambiguous in a comma-joined list.
 */
const CODE_PATTERN = /^[^\s,]+$/

/**
 * Selected codes, carrying the NAME for display.
 *
 * The label used to be the code itself, which made `getOptionLabel` on the properties field
 * inert: it faithfully returned what it was handed, so the chips showed the BFF's encrypted
 * `acc_…` handle while the dropdown one line below listed the same properties by name. Room
 * types and rate plans looked correct only because their free-solo field resolves labels
 * separately — the same lookup, written out a second time.
 *
 * Falls back to the code when it matches no option, for the same reason that path does: a
 * property that belongs to a record outside the loaded page should render as itself rather
 * than blank.
 */
const toOptions = (codes: string[], options: ScopeOption[]): ScopeOption[] => {
  const labelByCode = new Map(
    options.map((option) => [option.value, option.label])
  )
  return codes.map((code) => ({
    value: code,
    label: labelByCode.get(code) ?? code
  }))
}

/**
 * Where the promotion applies.
 *
 * Every field here is "empty means ALL", which is the opposite of what a blank field
 * usually implies, so each one says so in its helper text rather than relying on the
 * operator knowing the convention.
 *
 * Room types and rate plans accept FREE TEXT as well as a picked option. Their queries are
 * keyed on a single `propertyCode`, so the list can only be fetched once exactly one
 * property is chosen — but the codes circulate independently of this screen, and an
 * operator who knows one should not be blocked from typing it because the list behind it
 * cannot be narrowed yet.
 */
const ScopeSection: React.FC<ScopeSectionProps> = ({
  propertyCodes,
  roomTypeCodes,
  ratePlanCodes,
  consumableVia,
  onChange,
  propertyOptions,
  roomTypeOptions,
  ratePlanOptions,
  propertiesLoading,
  childrenLoading,
  channelField
}) => {
  const classes = useStyles()

  const childHelp =
    propertyCodes.length === 1
      ? getTranslate('Leave empty for all. Pick from the list or type a code.')
      : propertyCodes.length === 0
      ? getTranslate(
          'Leave empty for all. Choose one property to load the list, or type a code.'
        )
      : getTranslate(
          'Leave empty for all. The list covers the first property; type a code for the others.'
        )

  // Properties come from a real list and nothing else is valid, so this one is a plain
  // picker. Room types and rate plans are free-solo below.
  const propertiesField = (
    <AutocompleteWithLabel
      multiple
      fullWidth
      label={getTranslate('Properties')}
      helpertext={getTranslate('Leave empty for all properties.')}
      loading={propertiesLoading}
      value={toOptions(propertyCodes, propertyOptions)}
      options={propertyOptions}
      getOptionLabel={(option: ScopeOption) => option.label}
      getOptionSelected={(option: ScopeOption, selected: ScopeOption) =>
        option.value === selected.value
      }
      onChange={(_event: unknown, selected: ScopeOption[]) =>
        onChange(
          'propertyCodes',
          selected.map((option) => option.value)
        )
      }
    />
  )

  /**
   * A free-solo picker whose VALUES are codes and whose DISPLAY is the name.
   *
   * The option list stays bare codes, because free text and labelled option objects cannot
   * both work here: what the operator types IS the stored value, so a typed entry and a
   * picked one have to be the same kind of thing.
   *
   * An earlier version stopped there and rendered the code itself. That reasoning assumed
   * the code was something a human could read — but `roomTypeCode` and `ratePlanCode` are
   * encrypted by the BFF's id codec (their `parent*` variants are on its skip list; these
   * are not), so the field offered a list of `tok_…` blobs. Correct in every respect except
   * being usable.
   *
   * `getOptionLabel` resolves a code to its name for DISPLAY only, leaving the stored value
   * untouched — so the constraint above still holds. A code with no matching option (typed
   * by hand, or belonging to a record not in the loaded page) falls back to showing itself
   * rather than rendering blank.
   */
  const codesField = (
    key: 'roomTypeCodes' | 'ratePlanCodes',
    label: string,
    placeholder: string,
    value: string[],
    options: ScopeOption[]
  ) => {
    const labelByCode = new Map(
      options.map((option) => [option.value, option.label])
    )

    return (
      <FreeSoloAutocompleteWithLabel
        multiple
        fullWidth
        size="small"
        inputSize="small"
        name={key}
        label={getTranslate(label)}
        placeholder={getTranslate(placeholder)}
        message={childHelp}
        regexPattern={CODE_PATTERN}
        loading={childrenLoading}
        value={value}
        options={options.map((option) => option.value)}
        getOptionLabel={(code: string) => labelByCode.get(code) ?? code}
        onChange={(_event: unknown, selected: string[]) =>
          onChange(key, selected.map((entry) => entry.trim()).filter(Boolean))
        }
      />
    )
  }

  return (
    <div className={classes.section}>
      <SectionTitle title="Where it applies" marginBottom />
      <Typography variant="caption" className={classes.note}>
        {getTranslate(
          'An empty field means the promotion applies to everything in that dimension.'
        )}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          {propertiesField}
        </Grid>
        <Grid item xs={12} sm={4}>
          {codesField(
            'roomTypeCodes',
            'Room types',
            'Search or type a room type code',
            roomTypeCodes,
            roomTypeOptions
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {codesField(
            'ratePlanCodes',
            'Rate plans',
            'Search or type a rate plan code',
            ratePlanCodes,
            ratePlanOptions
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {channelField}
        </Grid>
        <Grid item xs={12} sm={8}>
          <AutocompleteWithLabel
            multiple
            fullWidth
            label={getTranslate('Booking surface')}
            helpertext={getTranslate('Leave empty for all booking surfaces.')}
            value={CONSUMABLE_VIA.filter((option) =>
              consumableVia.includes(option.value)
            )}
            options={CONSUMABLE_VIA}
            getOptionLabel={(option: ScopeOption) => getTranslate(option.label)}
            getOptionSelected={(option: ScopeOption, selected: ScopeOption) =>
              option.value === selected.value
            }
            onChange={(_event: unknown, selected: ScopeOption[]) =>
              onChange(
                'consumableVia',
                selected.map((option) => option.value)
              )
            }
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default ScopeSection
export { CONSUMABLE_VIA }
